import React from 'react';
import { motion } from 'framer-motion';
import { COMPANY, addressOneLine } from '../data/company';

const PrivacyMyOnlineRadio = () => {
  return (
    <div className="pt-24 pb-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 legal-content">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <span className="text-accent-indigo font-semibold tracking-wider text-sm uppercase mb-2 block">App Specific Policy</span>
        <h1>My Online Radio Privacy Policy</h1>
        <p className="text-sm border-l-2 border-accent-indigo pl-4 mb-8">
          Last Updated: September 2026 · Applies to version 2.4.0
        </p>

        <h2>The short version</h2>
        <p>
          My Online Radio collects nothing about you. It talks to three fixed Radio Browser servers
          to search stations, and to whichever station's own server you choose to stream from. There
          is no account, no analytics, and nothing is ever sent to us.
        </p>

        <h2>1. Who is responsible</h2>
        <p>
          The publisher is <strong>{COMPANY.legalName}</strong>, a company registered in
          {' '}{COMPANY.jurisdiction} under company number <strong>{COMPANY.number}</strong>,
          registered office {addressOneLine}. {COMPANY.tradingName} is a trading name of that company.
        </p>

        <h2>2. What the extension can see</h2>
        <p>
          The extension has no <code>tabs</code> or <code>activeTab</code> permission and no
          <code>host_permissions</code> beyond the three Radio Browser API mirrors it calls. It
          cannot read the address, title or content of any page you have open, and never injects a
          script into one.
        </p>

        <h2>3. What is stored, and where</h2>
        <p>
          Two items are kept on your own device, never transmitted anywhere:
        </p>
        <ul>
          <li><code>myOnlineRadio.favorites.v1</code>, in <code>chrome.storage.local</code> — the stations you have starred.</li>
          <li><code>myOnlineRadio.playerState.v1</code>, in <code>chrome.storage.session</code> — which station is playing, its volume and mute state, kept only for the current browser session so the player survives the popup closing.</li>
        </ul>
        <p>
          Neither is synced to a Google account. Removing the extension deletes both.
        </p>

        <h2>4. Requests the extension makes</h2>
        <p>
          Two different things happen when you use the extension, and they go to two different
          places:
        </p>
        <ul>
          <li>
            <strong>Searching, browsing by country and loading the country list</strong> call one of
            three fixed servers — <code>de1</code>, <code>nl1</code> and <code>at1</code> at
            {' '}<code>api.radio-browser.info</code> — the public, community-run Radio Browser project.
            Playing a station also sends that station's id to the same servers, an anonymous play
            count with nothing that identifies you attached.
          </li>
          <li>
            <strong>Streaming the audio itself</strong> connects directly from your browser to that
            station's own server, using the address Radio Browser lists for it. That connection is
            exactly the same as opening the station's stream URL in a browser tab: it goes straight
            to the broadcaster, not through us or through Radio Browser.
          </li>
        </ul>
        <p>
          Every request carries a <code>User-Agent</code> identifying the extension and its version.
          No cookies, no browsing history and no personal information are ever included.
        </p>

        <h2>5. What is never done</h2>
        <ul>
          <li>No analytics, telemetry, crash reporting or advertising identifiers.</li>
          <li>No account, sign-in or email address is required or accepted.</li>
          <li>Nothing is sold or shared, because nothing about you is collected.</li>
          <li>No script is injected into any page you visit, and no page content is read.</li>
        </ul>

        <h2>6. Permissions, and why each one exists</h2>
        <ul>
          <li><code>storage</code> — save your favorites and the current player state locally.</li>
          <li><code>offscreen</code> — keep the audio element alive after the popup closes, since Manifest V3 tears down the popup's own page the instant it closes.</li>
        </ul>
        <p>
          <code>host_permissions</code> names exactly the three Radio Browser mirrors above. There is
          no broader host access, and the extension cannot be granted one silently: any change to
          that list requires a new version reviewed by the Chrome Web Store.
        </p>

        <h2>7. Third parties</h2>
        <p>
          Radio Browser is an independent, community-run project, not operated by us. Its own
          servers see the requests described in section 4, the same as any web request they receive
          from any client. Streaming a station also means your browser connects directly to that
          station's own server, which sees that connection the same way it would if you opened the
          stream in a tab yourself. Neither relationship is something this extension controls.
        </p>

        <h2>8. Children</h2>
        <p>
          The extension is not directed at children and collects no personal information from
          anyone.
        </p>

        <h2>9. Changes</h2>
        <p>
          Any future change to this policy will ship with a version update and a revised date at the
          top of this page.
        </p>

        <h2>10. Contact</h2>
        <p>
          Questions about this policy: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>, or
          write to us at the registered office above.
        </p>
      </motion.div>
    </div>
  );
};

export default PrivacyMyOnlineRadio;
